import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { CheckIcon, LoaderIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
import { DS } from '../ds';
import { AlinovaConstellation, SmileDivider } from '../Visuals';
import { Btn, Label, P } from '../Primitives';
const EMAILJS_SERVICE = 'service_g4qrd3j';
const EMAILJS_TEMPLATE = 'template_vuw3rok';
const EMAILJS_KEY = 'a3z7AOkt8EwKXU8c-';
type Status = 'idle' | 'loading' | 'success' | 'error';
interface FormState {
  name: string;
  email: string;
  phone: string;
  modality: string;
}
const modalities = [{
  value: 'fabricacion',
  label: 'Solo fabricación'
}, {
  value: 'integral',
  label: 'Solución integral'
}, {
  value: 'flexible',
  label: 'Pedido flexible'
}, {
  value: 'consulta',
  label: 'Aún tengo dudas'
}];
const inputBase: React.CSSProperties = {
  width: '100%',
  padding: '13px 16px',
  borderRadius: '12px',
  border: `1px solid ${DS.color.border}`,
  background: DS.color.white,
  color: DS.color.ink,
  fontFamily: DS.font.body,
  fontSize: '14px',
  outline: 'none',
  transition: `all 0.22s ${DS.ease.smooth}`,
  boxSizing: 'border-box'
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: DS.font.body,
  fontSize: '12px',
  fontWeight: 600,
  color: DS.color.muted,
  marginBottom: '7px'
};
export const ContactSection = () => {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    modality: 'integral'
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const set = (key: keyof FormState, value: string) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
    if (errors[key]) setErrors(current => ({
      ...current,
      [key]: undefined
    }));
    if (status === 'error') setStatus('idle');
  };
  const validate = () => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = 'Ingresa tu nombre';
    if (!form.email.trim()) next.email = 'Ingresa tu correo';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Correo no válido';
    if (!form.phone.trim()) next.phone = 'Ingresa un teléfono de contacto';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (status === 'loading' || !validate()) return;
    const modalityLabel = modalities.find(m => m.value === form.modality)?.label ?? form.modality;
    setStatus('loading');
    try {
      await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        modalidad: modalityLabel
      }, EMAILJS_KEY);
      setStatus('success');
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus('error');
    }
  };
  const focusStyle = (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    event.currentTarget.style.borderColor = DS.color.aqua;
    event.currentTarget.style.background = DS.color.mint;
    event.currentTarget.style.boxShadow = '0 0 0 3px rgba(75,199,210,0.12)';
  };
  const blurStyle = (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    event.currentTarget.style.borderColor = DS.color.border;
    event.currentTarget.style.background = DS.color.white;
    event.currentTarget.style.boxShadow = 'none';
  };
  const resetForm = () => {
    setStatus('idle');
    setErrors({});
    setForm({
      name: '',
      email: '',
      phone: '',
      modality: 'integral'
    });
  };
  return <section id="contacto" style={{
    background: DS.color.ivory,
    position: 'relative',
    overflow: 'hidden'
  }} data-id="element-324" data-mp="35em4ubcimgny">
      <SmileDivider fromColor={DS.color.linen} toColor={DS.color.ivory} data-id="element-325" data-mp="13m7q1l59psbr" />

      {/* Grid CAD sutil */}
      <div aria-hidden="true" style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: 'linear-gradient(rgba(26,95,115,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(26,95,115,0.025) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
      pointerEvents: 'none',
      zIndex: 0
    }} data-id="element-326" data-mp="17l6tf1bcph13" />

      <div style={{
      padding: '40px 24px 96px',
      position: 'relative',
      zIndex: 1
    }} data-id="element-327" data-mp="3xjkfb4z1z7m">
        <div style={{
        maxWidth: '1100px',
        margin: '0 auto'
      }} data-id="element-328" data-mp="3w2m0t0iqsfol">
          <div className="col-2" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '64px',
          alignItems: 'center'
        }} data-id="element-329" data-mp="3w46xowsj4apm">
            {/* Columna izquierda — texto */}
            <div style={{
            position: 'relative'
          }} data-id="element-330" data-mp="3hfaouxinetny">
              <AlinovaConstellation width={320} height={320} opacity={0.05} style={{
              position: 'absolute',
              top: '-70px',
              left: '-70px',
              pointerEvents: 'none'
            }} data-id="element-331" data-mp="3cemohl8j50h2" />
              <div style={{
              position: 'relative',
              zIndex: 1
            }} data-id="element-332" data-mp="1ruras30pzrhi">
                <Label data-id="element-333" data-mp="1wuxa85d8eia5">Empecemos</Label>
                <h2 style={{
                fontFamily: DS.font.heading,
                fontWeight: 700,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                background: `linear-gradient(135deg, ${DS.color.aqua}, ${DS.color.teal})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: 1.12,
                letterSpacing: '-0.025em',
                marginBottom: '20px'
              }} data-id="element-334" data-mp="1wvymgd5zhatg">
                  Tu primer caso empieza aquí.
                </h2>
                <P size="lg" maxWidth="420px" data-id="element-335" data-mp="qel5flfwm25l">
                  Un asesor Alinova te guía en cada paso. Respuesta en menos de 24h.
                </P>

                <a href={`tel:+${DS.phone}`} style={{
                marginTop: '22px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: DS.color.teal,
                textDecoration: 'none',
                fontFamily: DS.font.body,
                fontSize: '14px',
                fontWeight: 700
              }} data-id="element-336" data-mp="icasnrsu01eh">
                  <PhoneIcon size={15} aria-hidden="true" data-id="element-337" data-mp="3rlon9yiw13ga" />
                  +57 301 653 9065
                </a>

                <div style={{
                marginTop: '36px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }} data-id="element-338" data-mp="4evxs7jt71f4">
                  {['Respuesta en menos de 24h hábiles', 'Análisis de viabilidad del caso', 'Acompañamiento dedicado en cada etapa'].map(item => <div key={item} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }} data-id="element-339" data-mp="2xqmn5p4e7pm7">
                      <span aria-hidden="true" style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: DS.color.mint,
                    border: `1px solid ${DS.color.aquaLight}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }} data-id="element-340" data-mp="32rttrkusuira">
                        <CheckIcon size={13} color={DS.color.teal} strokeWidth={3} data-id="element-341" data-mp="3co5d2ust48v8" />
                      </span>
                      <span style={{
                    fontFamily: DS.font.body,
                    fontSize: '14px',
                    color: DS.color.body
                  }} data-id="element-342" data-mp="48d7q63u7p61">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Columna derecha — formulario */}
            <div style={{
            background: DS.color.white,
            border: `1px solid ${DS.color.border}`,
            borderRadius: '24px',
            padding: '36px',
            position: 'relative',
            zIndex: 1,
            boxShadow: DS.shadow.card
          }} data-id="element-343" data-mp="1qelyu3b7cpml">
              {status === 'success' ? <div className="anim-fade-in" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              padding: '40px 24px'
            }} data-id="element-344" data-mp="2a2curgq32he6">
                  <div style={{
                width: '68px',
                height: '68px',
                borderRadius: '50%',
                marginBottom: '24px',
                background: `linear-gradient(135deg, ${DS.color.aquaLight}, ${DS.color.aqua})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: DS.shadow.glow,
                flexShrink: 0
              }} data-id="element-345" data-mp="3abkmkmia3eyx">
                    <CheckIcon size={32} color={DS.color.tealDeep} strokeWidth={3} data-id="element-346" data-mp="5vta2nh5n90b" />
                  </div>
                  <h3 style={{
                fontFamily: DS.font.heading,
                fontWeight: 700,
                fontSize: '1.5rem',
                color: DS.color.ink,
                marginBottom: '12px'
              }} data-id="element-347" data-mp="au36f1em8fni">
                    ¡Caso recibido!
                  </h3>
                  <p style={{
                fontFamily: DS.font.body,
                fontSize: '15px',
                color: DS.color.muted,
                lineHeight: 1.6,
                maxWidth: '300px',
                margin: '0 auto'
              }} data-id="element-348" data-mp="34dc4ti6r2pfq">
                    Gracias, {form.name.split(' ')[0] || 'doctor(a)'}. Un asesor de Alinova te contactará muy pronto.
                  </p>
                  <div style={{
                marginTop: '28px'
              }} data-id="element-349" data-mp="3s36ig1yw8i11">
                    <Btn variant="ghost" size="md" onClick={resetForm} data-id="element-350" data-mp="3s4hk9w8s3fpi">
                      Enviar otro caso
                    </Btn>
                  </div>
                </div> : <form onSubmit={handleSubmit} noValidate data-id="element-351" data-mp="5x9nvjfu6j5m">
                  {status === 'error' && <div role="alert" style={{
                marginBottom: '16px',
                padding: '12px 16px',
                borderRadius: '10px',
                background: 'rgba(224,106,106,0.08)',
                border: '1px solid rgba(224,106,106,0.25)',
                fontFamily: DS.font.body,
                fontSize: '13px',
                color: '#c0504d',
                textAlign: 'center'
              }} data-id="element-352" data-mp="9y37i0m8xbsp">
                      Hubo un error al enviar. Por favor intenta de nuevo.
                    </div>}

                  <div style={{
                marginBottom: '14px'
              }} data-id="element-353" data-mp="24bxlsdehdwi3">
                    <label htmlFor="cf-name" style={labelStyle} data-id="element-354" data-mp="1zfgy1kb71k5j">Nombre *</label>
                    <input id="cf-name" value={form.name} onChange={e => set('name', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} placeholder="Dr(a). Nombre completo" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'cf-name-error' : undefined} style={{
                  ...inputBase,
                  borderColor: errors.name ? '#e06a6a' : DS.color.border
                }} data-id="element-355" data-mp="2wtt5g1d78jkz" />
                    {errors.name && <span id="cf-name-error" style={errorStyle} data-id="element-356" data-mp="bgwmwltyiy4r">{errors.name}</span>}
                  </div>

                  <div className="col-2" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '14px',
                marginBottom: '14px'
              }} data-id="element-357" data-mp="3tnb1mn01vc47">
                    <div data-id="element-358" data-mp="1wt7uc0vmkm72">
                      <label htmlFor="cf-email" style={labelStyle} data-id="element-359" data-mp="1wtr42ub4g4a0">Correo *</label>
                      <input id="cf-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} placeholder="tu@correo.com" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'cf-email-error' : undefined} style={{
                    ...inputBase,
                    borderColor: errors.email ? '#e06a6a' : DS.color.border
                  }} data-id="element-360" data-mp="26yzcepmwj2zv" />
                      {errors.email && <span id="cf-email-error" style={errorStyle} data-id="element-361" data-mp="14b2vlwrn2qxu">{errors.email}</span>}
                    </div>
                    <div data-id="element-362" data-mp="10aso2fo77sgv">
                      <label htmlFor="cf-phone" style={labelStyle} data-id="element-363" data-mp="10bk9wyrzubty">Teléfono *</label>
                      <input id="cf-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} placeholder="Tu número de contacto" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'cf-phone-error' : undefined} style={{
                    ...inputBase,
                    borderColor: errors.phone ? '#e06a6a' : DS.color.border
                  }} data-id="element-364" data-mp="11qemu42ardxr" />
                      {errors.phone && <span id="cf-phone-error" style={errorStyle} data-id="element-365" data-mp="3vidbf4qg2p2s">{errors.phone}</span>}
                    </div>
                  </div>

                  <div style={{
                marginBottom: '28px'
              }} data-id="element-366" data-mp="49fzp038ysp0">
                    <label htmlFor="cf-modality" style={labelStyle} data-id="element-367" data-mp="3swchv5fm6ibt">Modalidad de interés</label>
                    <select id="cf-modality" value={form.modality} onChange={e => set('modality', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} style={{
                  ...inputBase,
                  cursor: 'pointer',
                  appearance: 'none',
                  color: DS.color.ink
                }} data-id="element-368" data-mp="3nzcgh7tpm3r1">
                      {modalities.map(m => <option key={m.value} value={m.value} style={{
                    color: DS.color.ink
                  }} data-id="element-369" data-mp="2v9x34beknyry">
                          {m.label}
                        </option>)}
                    </select>
                  </div>

                  <Btn variant="aqua" size="lg" fullWidth type="submit" disabled={status === 'loading'} data-id="element-370" data-mp="2maa1bohxkqyd">
                    {status === 'loading' ? <><LoaderIcon size={18} className="anim-rotate" data-id="element-371" data-mp="2gmyawe7w50c6" /> Enviando...</> : 'Empezar mi primer caso'}
                  </Btn>

                  <a href={DS.wa} target="_blank" rel="noopener noreferrer" style={{
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '9px',
                color: DS.color.teal,
                fontFamily: DS.font.body,
                fontSize: '14px',
                fontWeight: 700,
                textDecoration: 'none',
                opacity: 0.85,
                transition: `opacity 0.2s ${DS.ease.smooth}`
              }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.85'} data-id="element-372" data-mp="27m07ny5a0wun">
                    <MessageCircleIcon size={17} aria-hidden="true" data-id="element-373" data-mp="1gei5ptrbeq5k" />
                    Prefiero continuar por WhatsApp
                  </a>
                </form>}
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const errorStyle: React.CSSProperties = {
  fontFamily: DS.font.body,
  fontSize: '11px',
  color: '#c0504d'
};